import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ApplicationFacade } from '@app/store/facade/application.facade';
import { catchError, EMPTY, filter, finalize, firstValueFrom, from, Subject, switchMap, take, takeUntil, tap, throwError } from 'rxjs';
import { ProjectDataDexieService } from '@pwa/indexed-db/dexie-wrapper/project-data-dexie.service';
import { SyncDataDexieService } from '@pwa/indexed-db/dexie-wrapper/sync-data-dexie.service';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { environment } from '@environments/environment';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { OfflineDataService } from '@pwa/services/offline-data.service';
import { StaticDataDexieService } from '@pwa/indexed-db/dexie-wrapper/static-data-dexie.service';
import { IdTokenClaimsWithPolicyId } from '@shared/interfaces/id-token-claims-with-policy-id.interface';
import { DownloadDataService } from '@pwa/services/download/download-data.service';
import { PhotoDataDexieService } from '@pwa/indexed-db/dexie-wrapper/photo-data-dexie.service';
import { HeaderConfigService } from '@app/modules/project-details/core/services/header-config.service';
import { translate } from '@jsverse/transloco';
import { UserSettingsService } from '@shared/services/user/user-settings.service';
import { JobSchedulerIdbService } from '@pwa/indexed-db/services/static-data/job-scheduler-idb.service';
import { MasterDataUpdateSchedulerService } from '@shared/services/master-data-update-scheduler/master-data-update-scheduler.service';
import { SnackbarService } from '@shared/services/snackbar/snackbar.service';
import { AuthService } from '@shared/services/auth/auth.service';
import { ProjectService } from '@app/modules/project-details/core/services/project.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [ApplicationFacade],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading: boolean = true;
  isIframe: boolean = false;
  loginDisplay: boolean = false;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private readonly appDataIndexedDbService: ProjectDataDexieService,
    private readonly staticDataIndexedDbService: StaticDataDexieService,
    private readonly syncDataIndexedDbService: SyncDataDexieService,
    private readonly photoDataDexieService: PhotoDataDexieService,
    private readonly offlineDataService: OfflineDataService,
    private readonly jobSchedulerIdbService: JobSchedulerIdbService,
    private readonly downloadDataService: DownloadDataService,
    private readonly snackbarService: SnackbarService,
    private headerConfigService: HeaderConfigService,
    private cdr: ChangeDetectorRef,
    readonly applicationFacade: ApplicationFacade,
    private userSettingsService: UserSettingsService,
    private scheduler: MasterDataUpdateSchedulerService,
    private authService: AuthService,
    private projectService: ProjectService
  ) {
    this.applicationFacade.checkNetworkStatus();
    this.appDataIndexedDbService.initDb();
    this.syncDataIndexedDbService.initDb();
    this.staticDataIndexedDbService.initDb();
    this.photoDataDexieService.initDb();
  }

  ngOnInit(): void {
    // As soon as the application has initialized, show the splash screen
    this.applicationFacade.startLoading();

    // Subscribe to the loading state observable from the facade and update the local loading property
    this.applicationFacade.isAppLoading$.pipe(takeUntil(this.onDestroy$)).subscribe((loading) => {
      this.isLoading = loading;

      // Prevents the NG0100 error when `loading` has a different value after Angular has completed the change detection
      this.cdr.detectChanges();
    });

    // Check if data is downloaded only once at initialization
    // Only trigger the download process when the app is online and prevent redundant downloads
    this.offlineDataService
      .areMasterDataDownloaded()
      .pipe(
        take(1), // Take only the first emission to check just once
        switchMap((isDownloaded) => {
          if (isDownloaded) {
            // Data already downloaded, do nothing
            return EMPTY;
          } else {
            // Data not downloaded, download if online
            return this.applicationFacade.isOnline$.pipe(
              filter((isOnline) => isOnline),
              take(1), // Take only the first time we're online
              switchMap(() => {
                this.headerConfigService.shouldDisplaySpinner.next(true);
                this.headerConfigService.spinnerTooltip.next('header.tooltip-downloading-offline-data-in-progress');
                this.snackbarService.handleInfo(translate('snacks.download-in-progress-preparing-offline-data'));

                return from(
                  Promise.all([
                    this.downloadDataService.getAndStoreMasterDataProducts(),
                    this.downloadDataService.getAndStoreReferences(),
                    this.downloadDataService.getAndStoreRestrictions(),
                    firstValueFrom(this.offlineDataService.downloadAndPersistStaticDataEMDNEntries()),
                    firstValueFrom(this.jobSchedulerIdbService.updateSynchronizationJobSchedulerLastUpdatedDateToToday()),
                  ])
                ).pipe(
                  tap(() => this.snackbarService.handleSuccess(translate('snacks.static-data-for-offline-usage-downloaded-successfully'))),
                  catchError((error) => {
                    this.snackbarService.handleError(translate('snacks.static-data-for-offline-usage-download-failed'));
                    return throwError(() => error);
                  }),
                  finalize(() => this.headerConfigService.shouldDisplaySpinner.next(false))
                );
              })
            );
          }
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();

    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    /**
     * MASSIVE TODO: Make this the only MSAL subscription, otherwise some paths do not get the User.
     *              - create a User interface;
     *              - create a User service with a User Subject in it;
     *              - check for the subject in the app-header.
     * https://stackoverflow.com/questions/61460789/broadcast-service-not-working-with-msal-in-angular
     */
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.clearUserScopedCaches();
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();

        // Stop the loader & hide the splash screen once MSAL finishes resolving auth state
        this.applicationFacade.stopLoading();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idToken: IdTokenClaimsWithPolicyId = payload.idTokenClaims;
        this.authService.setIdTokenHint(payload.idToken);

        if (idToken.acr === environment.b2cPolicies.names.signUpSignIn || idToken.tfp === environment.b2cPolicies.names.signUpSignIn) {
          const activeAccount: AccountInfo | null = this.msalService.instance.getActiveAccount();
          if (activeAccount?.homeAccountId !== payload.account.homeAccountId) this.clearUserScopedCaches();

          this.msalService.instance.setActiveAccount(payload.account);
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this.onDestroy$)
      )
      .subscribe((result: EventMessage) => {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
        }
      });

    // A call to get the stored User language and then set it as the application one via Transloco
    this.userSettingsService.getAndSetUserLanguage();

    // Check if the Users haven't updated their master data for more than 14 days and prompt them with a dialog to do it
    this.scheduler.checkLastUpdateDateAndPromptIfNeeded();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Clears in-memory data that belongs to the signed-in User (roles and fetched Projects), so a different account
   * never reuses them.
   */
  private clearUserScopedCaches(): void {
    this.authService.clearRolesCache();
    this.projectService.clearProjectCache();
  }

  setLoginDisplay(): void {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.clearUserScopedCaches();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect(): void {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }
}
